import { Button, H6 } from "@sellar-io/components";
import { PaymentLinkGetByCodeResponseType } from "@sellar-io/api-v2/types";
import { formatCurrency, pluralize } from "@sellar-io/common";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useMatch, useNavigate } from "react-router";
import { useIntercom } from "react-use-intercom";
import { Icon, ProfilePicture, SpinnerLegacy } from "../../components";
import { useMenuDrawerContext } from "../../components/menu/MenuDrawerContextProvider.shared";
import { NavigationBar } from "../../components/navigation/NavigationBar";
import { LOGIN, PAYMENT_LINK_REF } from "../../constants/routes";
import { useSellarUser } from "../../hooks";
import { apiV2AssertError } from "../../hooks/api/v2/apiV2Request";
import { usePaymentLinkGetByCode } from "../../hooks/api/v2/paymentLink/usePaymentLinkGetByCode";
import { usePaymentLinkPaymentStatusRefresh } from "../../hooks/api/v2/paymentLink/usePaymentLinkPaymentStatusRefresh";
import { StripeProvider } from "../stockList/checkoutV2/lib/StripeProvider";
import { PoweredBySellar } from "../stockList/categoryPage/Storefront/PoweredBySellar";
import { PaymentLinkDrawer } from "./components/PaymentLinkDrawer";
import { InvoiceDrawerTabs } from "./components/PaymentLinkDrawer.shared";
import { PaymentLinkPaymentForm } from "./components/PaymentLinkPaymentForm";

const POLLING_INTERVAL_MS = 5000;
const POLLING_WINDOW_MINUTES = 10;

export const PaymentLink = () => {
  const navigate = useNavigate();
  const { user } = useSellarUser();
  const intercom = useIntercom();
  const { setDisplayTypeOverride } = useMenuDrawerContext();

  useEffect(() => {
    setDisplayTypeOverride("hidden");
    return () => {
      setDisplayTypeOverride(undefined);
    };
  }, [setDisplayTypeOverride]);
  const { code } = useMatch(PAYMENT_LINK_REF.pattern)?.params ?? {};
  if (!code) throw new Error("code is required");
  const refreshPaymentStatus = usePaymentLinkPaymentStatusRefresh(code);
  const refreshRequestedRef = useRef(false);

  const paymentLinkQuery = usePaymentLinkGetByCode(code, {
    refetchInterval: (response) => {
      const data = response.state.data;
      if (!data) return false;
      if (data.Order.paidAt || data.Order.cancelledAt) return false;

      const latestPayment = data.Order.PaymentList[0];
      if (!latestPayment || latestPayment.status !== "pending") return false;

      const minutesSinceCreated = dayjs().diff(
        dayjs(latestPayment.createdAt),
        "minutes",
      );
      if (minutesSinceCreated >= POLLING_WINDOW_MINUTES) return false;

      return POLLING_INTERVAL_MS;
    },
    refetchOnWindowFocus: false,
  });

  const latestPayment = paymentLinkQuery.data?.Order.PaymentList[0];
  useEffect(() => {
    if (!latestPayment || latestPayment.status !== "pending") return;

    const refreshAt = dayjs(latestPayment.createdAt).add(
      POLLING_WINDOW_MINUTES,
      "minutes",
    );
    const delay = Math.max(0, refreshAt.diff(dayjs(), "milliseconds"));
    const timeout = window.setTimeout(() => {
      if (refreshRequestedRef.current) return;
      refreshRequestedRef.current = true;
      refreshPaymentStatus.mutate();
    }, delay);

    return () => window.clearTimeout(timeout);
  }, [latestPayment, refreshPaymentStatus]);

  if (paymentLinkQuery.isPending) {
    return (
      <div className="flex w-full justify-center">
        <SpinnerLegacy />
      </div>
    );
  }

  if (paymentLinkQuery.isError) {
    throw new Error(
      apiV2AssertError(paymentLinkQuery.error)?.response?.data.message ??
        "Unknown error",
    );
  }

  const data = paymentLinkQuery.data;

  if (!data.Order.Supplier.stripeAccountId) {
    throw new Error("Stripe account id is required");
  }
  return (
    <>
      <NavigationBar
        title={"Payment Link"}
        leftIcon={"INFORMATION_OUTLINE"}
        leftIconText={"Support"}
        onClickLeftIcon={() => intercom.show()}
        rightComponent={
          user ? null : (
            <Button
              color={"transparent"}
              onClick={() => {
                navigate(LOGIN);
              }}
            >
              <Icon icon={"LOGIN_VARIANT"} />
              <span className="hidden sm:block">Login</span>
            </Button>
          )
        }
      />
      <div className="flex w-full justify-center">
        <StripeProvider
          stripeAccountId={data.Order.Supplier.stripeAccountId}
          amount={data.Order.price}
        >
          <PaymentLinkContent
            order={data.Order}
            code={code}
            connectionPaymentTerms={data.connectionPaymentTerms}
          />
        </StripeProvider>
      </div>
    </>
  );
};

const STATUS_BANNER_STYLES = {
  success: {
    bg: "bg-emerald-100",
    outline: "outline-emerald-300",
    text: "text-emerald-800",
    icon: "CHECK_CIRCLE" as const,
    iconColor: "text-emerald-800",
  },
  failed: {
    bg: "bg-red-100",
    outline: "outline-red-300",
    text: "text-red-800",
    icon: "CLOSE" as const,
    iconColor: "text-red-800",
  },
  cancelled: {
    bg: "bg-red-100",
    outline: "outline-red-300",
    text: "text-red-800",
    icon: "CLOSE" as const,
    iconColor: "text-red-800",
  },
};

const StatusBanner = ({
  variant,
  children,
}: {
  variant: "success" | "failed" | "cancelled";
  children: React.ReactNode;
}) => {
  const styles = STATUS_BANNER_STYLES[variant];
  return (
    <div
      className={`inline-flex items-center gap-2 self-stretch rounded-lg p-3 outline outline-1 outline-offset-[-1px] ${styles.bg} ${styles.outline}`}
    >
      <div className="flex flex-1 items-center justify-center">
        <div
          className={`flex-1 font-['Inter'] text-sm leading-5 font-normal ${styles.text}`}
        >
          {children}
        </div>
      </div>
      <Icon icon={styles.icon} className={styles.iconColor} />
    </div>
  );
};

const PaymentReceiptRow = ({
  label,
  value,
}: {
  label: string;
  value: string;
}) => (
  <div className="inline-flex items-center justify-between self-stretch">
    <div className="font-['Inter'] text-sm leading-5 font-normal text-zinc-800">
      {label}
    </div>
    <div className="font-['Inter'] text-sm leading-5 font-semibold text-zinc-800">
      {value}
    </div>
  </div>
);

const PaymentReceipt = ({
  invoiceNumber,
  paymentDate,
}: {
  invoiceNumber: string | null;
  paymentDate: Date | string | null;
}) => (
  <div className="inline-flex flex-col items-center justify-start gap-2.5 self-stretch rounded-lg bg-emerald-100 p-4 outline outline-1 outline-offset-[-1px] outline-emerald-300">
    <div className="flex flex-col items-start justify-start gap-2 self-stretch">
      {invoiceNumber && (
        <PaymentReceiptRow label="Invoice number" value={invoiceNumber} />
      )}
      {paymentDate && (
        <PaymentReceiptRow
          label="Payment date"
          value={dayjs(paymentDate).format("DD/MM/YYYY")}
        />
      )}
    </div>
  </div>
);

type PaymentState = "idle" | "pending" | "paid" | "failed";

const derivePaymentState = (
  order: PaymentLinkGetByCodeResponseType["Order"],
): PaymentState => {
  if (order.paidAt) return "paid";

  const latestPayment = order.PaymentList[0];
  if (latestPayment?.status === "paid") return "paid";
  if (latestPayment?.status === "pending") return "pending";
  if (latestPayment?.status === "failed") return "failed";

  return "idle";
};

const INVOICE_CREATION_STAGE_LABELS: Record<string, string> = {
  confirmed: "finalisation",
  dispatched: "dispatch",
  delivered: "delivery",
};

type ConnectionPaymentTerms = NonNullable<
  PaymentLinkGetByCodeResponseType["connectionPaymentTerms"]
>;

const formatCreditTermsLabel = (connection: ConnectionPaymentTerms) => {
  const parts: string[] = [];
  if (connection.paymentDueCalculateFrom === "endOfMonth") {
    parts.push(connection.paymentDue ? "EOM +" : "EOM");
  } else if (connection.paymentDueCalculateFrom === "endOfFollowingMonth") {
    parts.push(connection.paymentDue ? "EOFM +" : "EOFM");
  }
  if (connection.paymentDue) {
    parts.push(
      `${connection.paymentDue} ${pluralize("day", connection.paymentDue)}`,
    );
  }
  return parts.join(" ");
};

const getPaymentDueText = (
  order: PaymentLinkGetByCodeResponseType["Order"],
  connectionPaymentTerms: ConnectionPaymentTerms | null,
) => {
  if (order.paymentDue) {
    return `Payment due: ${dayjs(order.paymentDue).format("DD/MM/YY")}`;
  }

  if (connectionPaymentTerms?.paymentTerms === "credit") {
    const termsLabel = formatCreditTermsLabel(connectionPaymentTerms);
    const stageLabel =
      INVOICE_CREATION_STAGE_LABELS[order.Supplier.invoiceCreationStage] ??
      "finalisation";
    return `Payment due ${termsLabel} after ${stageLabel} of order`;
  }

  if (connectionPaymentTerms?.paymentTerms === "paymentOnDelivery") {
    return "Payment due on delivery";
  }

  return "Payment due before dispatch of order";
};

const PaymentLinkContent = ({
  order,
  code,
  connectionPaymentTerms,
}: {
  order: PaymentLinkGetByCodeResponseType["Order"];
  code: string;
  connectionPaymentTerms: ConnectionPaymentTerms | null;
}) => {
  const navigate = useNavigate();
  const [drawerActive, setDrawerActive] = useState(false);

  const supplier = order.Supplier;
  const supplierBusiness = supplier.Business;
  const supplierBusinessName = supplierBusiness.name;

  const paymentState = derivePaymentState(order);
  const isPaid = paymentState === "paid";
  const isPending = paymentState === "pending";
  const isCancelled = !!order.cancelledAt;
  const isPaymentFailed = paymentState === "failed";

  const canShowPaymentForm =
    !isPaid &&
    !isPending &&
    !isCancelled &&
    !!supplier.stripeAccountId &&
    (paymentState === "idle" || paymentState === "failed");

  return (
    <>
      <div className="flex w-[480px] flex-col gap-2 pt-8 pb-8">
        <div className="flex flex-col items-center justify-start rounded-lg shadow-[0px_16px_32px_0px_rgba(0,0,0,0.16)] outline outline-1 outline-zinc-300">
          <div className="flex flex-col items-center justify-start gap-6 self-stretch rounded-tl-lg rounded-tr-lg border-b border-zinc-300 bg-white px-4 py-6">
            <div className="flex w-full flex-col items-center justify-start">
              <div className="flex w-52 w-full flex-col items-center justify-start gap-1">
                <div className="flex flex-col items-center justify-start gap-3 self-stretch">
                  <ProfilePicture
                    name={supplierBusinessName}
                    logoUrl={supplierBusiness.logoUrl}
                    size={"xl"}
                  />
                  <div className="justify-center self-stretch text-center font-['Inter'] text-2xl leading-loose font-semibold text-zinc-800">
                    {supplierBusinessName}
                  </div>
                </div>
                <div className="my-2 flex flex-col items-center gap-2">
                  <H6>Order# {order.id}</H6>
                  <H6>
                    {order.Retailer.Business.name},{" "}
                    {order.DeliveryAddress.postalCode}
                  </H6>
                </div>
              </div>
            </div>
            <div className="flex w-full flex-col items-center justify-start gap-4">
              <div className="inline-flex w-full flex-col items-start justify-start gap-2 self-stretch rounded-lg bg-zinc-100 py-2">
                <div className="justify-center self-stretch text-center text-2xl leading-8 font-semibold text-zinc-800">
                  {formatCurrency(order.price)}
                </div>
                <div className="justify-center self-stretch text-center font-['Inter'] text-base leading-6 font-semibold text-zinc-800">
                  {getPaymentDueText(order, connectionPaymentTerms)}
                </div>
              </div>
              {isPaid && (
                <StatusBanner variant="success">
                  Payment successful
                </StatusBanner>
              )}
              {isCancelled && (
                <StatusBanner variant="cancelled">Order cancelled</StatusBanner>
              )}
              {isPaymentFailed && !isCancelled && (
                <StatusBanner variant="failed">
                  Payment failed – try again below
                </StatusBanner>
              )}
            </div>
          </div>
          <div className="inline-flex items-center justify-start self-stretch bg-white">
            <div
              className="flex flex-1 cursor-pointer items-center justify-center gap-1 rounded-b-lg bg-white p-4"
              onClick={() => {
                setDrawerActive(true);
              }}
            >
              <div className="flex items-center justify-center">
                <div className="justify-center text-center font-['Inter'] text-sm leading-tight font-semibold text-zinc-600">
                  View Order Details
                </div>
              </div>
              <Icon icon={"CHEVRON_RIGHT"} />
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-4 self-stretch rounded-lg bg-white p-4 shadow-[0px_16px_32px_0px_rgba(0,0,0,0.16)] outline outline-1 outline-zinc-300">
          {isPending && (
            <div className="flex flex-col items-center gap-2 py-2">
              <SpinnerLegacy />
              <div className="font-['Inter'] text-lg font-semibold text-zinc-800">
                Processing payment...
              </div>
              <div className="text-center text-sm text-zinc-600">
                Please wait while we confirm your payment.
              </div>
            </div>
          )}
          {canShowPaymentForm && (
            <PaymentLinkPaymentForm code={code} amount={order.price} />
          )}
          {isPaid && (
            <PaymentReceipt
              invoiceNumber={order.customInvoiceNumber}
              paymentDate={order.paidAt}
            />
          )}
          {(isPaid || isCancelled) && (
            <Button
              className="w-full"
              onClick={() => {
                navigate(LOGIN);
              }}
            >
              Back to the Market
            </Button>
          )}
        </div>
        <PoweredBySellar />
      </div>
      <PaymentLinkDrawer
        active={drawerActive}
        setActive={setDrawerActive}
        order={order}
        initialTab={InvoiceDrawerTabs.DETAILS}
        showComments={false}
      />
    </>
  );
};
